import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Slider,
  TextField,
  Button,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect } from "react";

const AllocationSection = ({
  bidData,
  rfq,
  groupedData,
  allocations,
  setAllocations,
  setIsValid,
}) => {
  const normalize = (str) => str?.replace(/\s+/g, " ").trim().toLowerCase();

  const allocateAllToVendor = (vendorToAllocate) => {
    const newAlloc = {};
    rfq.rfqWorkItems.forEach((item) => {
      newAlloc[item.itemOrder] = {};
      Object.keys(groupedData).forEach((vendor) => {
        newAlloc[item.itemOrder][vendor] = vendor === vendorToAllocate ? 100 : 0;
      });
    });
    setAllocations(newAlloc);
  };

  useEffect(() => {
    if (rfq && rfq.rfqWorkItems && groupedData && bidData) {
      const init = {};
      rfq.rfqWorkItems.forEach((item) => {
        init[item.itemOrder] = {};
        Object.keys(groupedData).forEach((vendorKey) => {
          const vendorBids = groupedData[vendorKey];
          const mostRecentBid = [...vendorBids].sort(
            (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
          )[0];

          const allocation = mostRecentBid
            ? mostRecentBid.bidWorkItems.find(
                (bw) =>
                  bw.code === item.code &&
                  normalize(bw.description) === normalize(item.description) &&
                  bw.quantity === item.quantity &&
                  bw.unit === item.unit &&
                  bw.gst === item.gst,
              )?.percentageAllotted || 0
            : 0;
          init[item.itemOrder][vendorKey] = allocation;
        });
      });
      setAllocations(init);
    }
  }, [rfq, groupedData, bidData, setAllocations]);

  const handleAllocationChange = (itemOrder, vendor, value) => {
    setAllocations((prev) => {
      const newAlloc = { ...prev };
      const totalBefore = Object.values(newAlloc[itemOrder]).reduce(
        (sum, v) => sum + v,
        0,
      );

      const totalAfter = totalBefore - newAlloc[itemOrder][vendor] + value;
      if (totalAfter <= 100) {
        newAlloc[itemOrder][vendor] = value;
      } else {
        newAlloc[itemOrder][vendor] += 100 - totalBefore;
      }
      return newAlloc;
    });
  };

  const isValidAllocation = rfq.rfqWorkItems.every((item) => {
    const total = Object.values(allocations[item.itemOrder] || {}).reduce(
      (sum, v) => sum + v,
      0,
    );
    return Math.round(total * 100) / 100 === 100;
  });

  useEffect(() => {
    setIsValid(isValidAllocation);
  }, [isValidAllocation, allocations]);

  if (
    !rfq ||
    !rfq.rfqWorkItems ||
    !groupedData ||
    Object.keys(groupedData).length === 0
  ) {
    return <Typography>Loading allocation data...</Typography>;
  }

  const vendorTotalsAmount = {};
  const vendorGSTAmount = {};
  Object.keys(groupedData).forEach((vendor) => {
    let totalAmount = 0;
    let totalTax = 0;
    rfq.rfqWorkItems.forEach((item) => {
      const percent = allocations[item.itemOrder]?.[vendor] || 0;

      const mostRecentBid = [...groupedData[vendor]].sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
      )[0];

      const rate =
        mostRecentBid?.bidWorkItems.find(
          (bw) =>
            bw.code === item.code &&
            normalize(bw.description) === normalize(item.description) &&
            bw.quantity === item.quantity &&
            bw.unit === item.unit &&
            bw.gst === item.gst,
        )?.rate || 0;

      const gst =
        mostRecentBid?.bidWorkItems.find(
          (bw) =>
            bw.code === item.code &&
            normalize(bw.description) === normalize(item.description) &&
            bw.quantity === item.quantity &&
            bw.unit === item.unit &&
            bw.gst === item.gst,
        )?.gst || 0;
      totalAmount += (percent / 100) * item.quantity * rate;
      const tax = ((percent / 100) * item.quantity * rate * gst) / 100;
      totalTax += tax;
    });
    vendorTotalsAmount[vendor] = totalAmount;
    vendorGSTAmount[vendor] = totalAmount + totalTax;
  });
  const groupedBySection = rfq.rfqWorkItems.reduce((acc, item) => {
    const section = item.section || "Uncategorized";
    acc[section] = acc[section] || [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <Box mt={4} p={2} sx={{ overflowX: "auto" }}>
      <table
        border={1}
        style={{
          borderCollapse: "collapse",
          minWidth: `${Object.keys(groupedData).length * 550}px`,
        }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: "start", padding: "20px", width: "12%" }}>
              Code
            </th>
            {Object.keys(groupedData).map((vendor) => (
              <th key={vendor}>
                {vendor}{" "}
                <Button
                  variant="outlined"
                  onClick={() => allocateAllToVendor(vendor)}
                  sx={{ ml: 1 }}
                >
                  Allot 100%
                </Button>
              </th>
            ))}
            <th>Total (%)</th>
          </tr>
        </thead>
        <tbody>
          {rfq.rfqWorkItems
            .sort((a, b) => a.itemOrder - b.itemOrder)
            .map((item) => {
              const totalRow = Object.values(
                allocations[item.itemOrder] || {},
              ).reduce((sum, v) => sum + v, 0);
              return (
                <tr key={item.itemOrder}>
                  <td style={{ padding: "20px" }}>{item.code}</td>
                  {Object.keys(groupedData).map((vendor) => {
                    const percent = allocations[item.itemOrder]?.[vendor] || 0;
                    const mostRecentBid = [...groupedData[vendor]].sort(
                      (a, b) =>
                        new Date(b.createdDate) - new Date(a.createdDate),
                    )[0];
                    const normalize = (str) =>
                      str?.replace(/\s+/g, " ").trim().toLowerCase();
                    const rate =
                      mostRecentBid?.bidWorkItems.find(
                        (bw) =>
                          bw.code === item.code &&
                          normalize(bw.description) ===
                            normalize(item.description) &&
                          bw.quantity === item.quantity &&
                          bw.unit === item.unit
                          // bw.gst === item.gst,
                      )?.rate || 0;
                    const amount = (percent / 100) * item.quantity * rate;
                    return (
                      <td
                        key={vendor}
                        style={{ padding: "8px", minWidth: "200px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                          }}
                        >
                          <Slider
                            size="small"
                            min={0}
                            max={100}
                            step={0.01}
                            value={percent}
                            onChange={(_, value) =>
                              handleAllocationChange(
                                item.itemOrder,
                                vendor,
                                Number(value),
                              )
                            }
                            valueLabelDisplay="auto"
                            sx={{ mb: 1, width: "70%" }}
                          />

                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <TextField
                              type="number"
                              size="small"
                              inputProps={{
                                step: "0.01",
                                min: 0,
                                max: 100,
                              }}
                              value={percent}
                              onChange={(e) => {
                                let newValue = parseFloat(e.target.value);
                                if (isNaN(newValue)) newValue = 0;
                                if (newValue > 100) newValue = 100;
                                const rounded = parseFloat(newValue.toFixed(2));
                                handleAllocationChange(
                                  item.itemOrder,
                                  vendor,
                                  rounded,
                                );
                              }}
                              sx={{ width: "80px" }}
                            />
                            <span>%</span>
                          </div>
                        </div>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", textAlign: "center" }}
                        >
                          {((percent / 100) * item.quantity).toFixed(2)} of{" "}
                          {item.quantity}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "center",
                            mt: 0.5,
                          }}
                        >
                          &#8377;{amount.toFixed(2)} (&#8377;{rate}/unit)
                        </Typography>
                      </td>
                    );
                  })}
                  <td
                    style={{
                      textAlign: "center",
                      color: totalRow === 100 ? "green" : "red",
                    }}
                  >
                    {totalRow.toFixed(2)}%
                  </td>
                </tr>
              );
            })}
          {/* Total per vendor */}
          <tr style={{ fontWeight: "bold" }}>
            <td style={{ padding: "20px" }}>Total Amount</td>
            {Object.keys(groupedData).map((vendor) => (
              <td key={vendor} style={{ textAlign: "center" }}>
                &#8377;{vendorTotalsAmount[vendor].toFixed(2)}
              </td>
            ))}
            <td style={{ textAlign: "center" }}>-</td>
          </tr>
          <tr style={{ fontWeight: "bold" }}>
            <td style={{ padding: "20px", width: "250px" }}>
              Total Amount (Including GST)
            </td>
            {Object.keys(groupedData).map((vendor) => (
              <td key={vendor} style={{ textAlign: "center" }}>
                &#8377;{vendorGSTAmount[vendor].toFixed(2)}
              </td>
            ))}
            <td style={{ textAlign: "center" }}>-</td>
          </tr>
        </tbody>
      </table>

      {!isValidAllocation && (
        <Typography color="error" mt={2} align="right">
          Total allocation per work item must be 100%!
        </Typography>
      )}
    </Box>
  );
};

export default AllocationSection;